import { GuideScreenshot } from '@/components/guide-screenshot';

export const meta = {
  title: 'WordCraft 网页版指南：保存文件、字体与浏览器故障排查',
  description:
    '了解 WordCraft 网页版的运行和保存方式，逐项排查下载位置、字体缺失、空白编辑器、快捷键与浏览器环境问题。',
  updated_at: '2026-11-05',
  guide: true,
  image: '/imgs/wordcraft/dark',
  imageAlt: 'WordCraft 深色模式与插入选项卡',
  category: '浏览器设置与排障',
  readingMinutes: 10,
  toc: [
    { id: 'web-basics', title: '理解网页版的运行方式' },
    { id: 'first-launch', title: '检查首次启动' },
    { id: 'saving', title: '分清两种保存结果' },
    { id: 'fonts', title: '解决字体与缺字问题' },
    { id: 'blank-screen', title: '排查空白编辑器' },
    { id: 'shortcuts', title: '检查快捷键与显示' },
    { id: 'desktop', title: '何时考虑桌面版' },
    { id: 'questions', title: '网页版常见问题' },
  ],
};

WordCraft 网页版可以直接通过浏览器打开，但正常使用仍然依赖图形环境、可用字体和成功的文件保存流程。把这些问题拆开，排查会容易得多：缺少字体不同于应用没有加载，取消保存窗口也不同于下载文件后找不到位置。

本文针对本站托管的版本，提供按顺序检查的方法。先打开 [WordCraft 在线页面](/online)，那里有正常启动和兼容模式入口。如果你需要的是完成一篇文档的写作步骤，可以读[在线写作指南](/wordcraft-online-guide)；[首页](/)同时提供编辑器与桌面下载入口。

<h2 id="web-basics">理解应用、当前文档与保存文件的区别</h2>

WordCraft 浏览器版本使用 WebAssembly。本站托管方式由服务器提供应用资源，编辑工作在浏览器里进行。打开网页编辑器，不等于进入一个有云端文件库、共享历史和多设备自动同步的文档服务。

本站构建基于 v0.7.0，并包含多语言字体与浏览器保存相关的本地修改。[构建说明](/app/NOTICE.md)记录了固定的上游版本和修改内容。[官方项目](https://github.com/storytold/wordcraft)可能已有更新发布，查看截图或反馈问题时，应先分清具体环境。

浏览器缓存可能让再次启动更快，但不能当作可靠的文档备份。缓存可能被回收，隐私窗口可能在结束时丢弃状态，不同浏览器配置也有各自的存储。真正需要保留的工作，应保存到自己能管理的位置并验证副本。

可以把过程分成三件事：浏览器下载的应用、当前在内存里打开的文档、最终保存到设备上的文件。重新看到应用界面，不证明文档已保存；磁盘上存在一个旧文件，也不证明最后一次修改已经写入。

<h2 id="first-launch">首次启动先测试一份小文档</h2>

使用较新的桌面浏览器打开在线页面，通过编辑器链接进入，等待应用与字体完成加载。使用内嵌编辑器时，如果窗口显得拥挤，可以单独打开编辑器标签页，获得更大空间，也更容易看到浏览器提示。

建立空白文档，输入一句容易识别的文字，例如“周四浏览器保存测试”。再输入一行真实工作会用到的其他语言字符或符号。这份小样本可以同时检查输入、字体显示和保存，不需要先导入复杂报告。

保存文档，找到生成的文件并重新打开。小测试成功、长报告失败，可能更值得检查报告本身的内容、布局或资源需求；小测试也失败，则先查看浏览器环境，不要不断尝试更大的文件。

<GuideScreenshot
  base="/imgs/wordcraft/hero"
  alt="WordCraft 编辑器显示功能区、可编辑文档页面和导航窗格"
  caption="图 1：正常加载后应能看到并操作文档区域。这是上游产品截图，本站浏览器版本的局部细节可能不同。图片来源：ArtCraft / WordCraft。"
/>

描述问题时，记录浏览器名称、操作系统、托管版本和出错阶段。“加载进度一直不结束”和“文件能打开但某个字体不见了”，对应不同的检查方向。简短而准确的描述，比只说“打不开”更有效。

<h2 id="saving">区分文件选择器与浏览器下载</h2>

本站版本保存时，可能打开文件选择窗口，也可能产生普通浏览器下载，具体取决于浏览器能力和策略。本地修改为文件选择器被策略拒绝的情况提供下载回退，但用户主动取消窗口时，仍然会取消保存，不会把取消理解成自动下载。

执行保存后，确认实际发生了哪一种结果。有选择窗口，就完成操作并记住所选位置；有下载，就在浏览器下载列表中找到它；两者都没有发生，则先保留编辑器，继续查看原因，不要假设保存已经静默完成。

<GuideScreenshot
  base="/imgs/wordcraft/backstage"
  alt="WordCraft 文件菜单里的保存、另存为和导出操作"
  caption="图 2：点击保存只是开始，浏览器文件流程完成后才算产生结果。请检查实际文件，而不是仅凭菜单动作判断。图片来源：ArtCraft / WordCraft。"
/>

重复下载可能产生带 `(1)`、`(2)` 后缀的文件。通过修改时间和内容确认哪份最新，再把它移动到明确的工作文件夹，主动重新命名。否则下一次编辑很可能从名字最简洁、实际却最旧的文件开始。

重要会话可以遵循一个小循环：保存、打开保存结果、检查最近改动、再继续或结束。文档末尾的新句子很适合验证版本，因为它通常不存在于早期草稿里。如果刚修改过表格或图片，也一起检查。

保存受阻时，不要在仍有未保存内容的情况下直接刷新。先找是否有被遮住的系统窗口、隐藏的浏览器提示或下载拦截通知。如果编辑器仍可用，可以先把关键文字另行保存在本地副本中，再调查文件流程。清理浏览器数据不应成为处理未保存文档的第一步。

<h2 id="fonts">分别处理字体替换与字符缺失</h2>

字体替换通常表现为字形或换行与预期不同；字符缺失可能表现为空框、缺字或某些字符显示异常。两者都与字体有关，但检查重点不同。一种字体能正常显示英文，并不代表它覆盖文档需要的中文、阿拉伯文或其他字符。

本站构建包含多语言回退字体，但不能自动把操作系统里安装的每一种字体都提供给浏览器编辑器。需要其他字体时，可以按本站编辑器说明，在字体菜单底部使用 Add Font，或拖入有使用许可的字体文件。支持的输入格式见[构建说明](/app/NOTICE.md)。

导入前先确认字体许可，以及它是否包含实际需要的字符。用包含常用文字、数字和标点的短句试验。多语言文档应分别输入真实句子，不能只凭字体名称判断覆盖范围。

更换字体后重新检查换行、表格宽度和页数。一个字体变化可能让表格变宽，也可能把标题挤到下一页。先确定字体，再修复版面上的次生变化，避免用手动空格掩盖根本问题。

还要区分界面语言和文档字体。把菜单切换到某种语言，不一定会改变导入段落使用的字体；添加一个文档字体，也不会自动翻译应用按钮。针对实际看到的问题检查相应设置，排查会更直接。

<h2 id="blank-screen">按顺序排查空白或异常编辑器</h2>

首先确认问题发生在什么时候。应用从未成功启动，先查看网络和加载界面的错误提示；应用已经正常打开，只有某个文档失败，则在保留当前工作后，用小文件测试。这样可以把启动问题与文档问题分开。

在线页面提供带 `?webgl` 的兼容模式链接。没有未保存文档风险时，可以用它尝试处理空白或绘制异常。它会在本站托管设置中选择 WebGL 渲染路径。这是一种排查手段，不意味着所有设备或组织策略都一定支持运行。

接下来可以在同一台电脑上换一个较新的浏览器，使用同一份小测试文件。每次只改一个变量并记录结果。如果同时换电脑、浏览器、文档和字体，即使成功，也很难知道到底是哪一步起了作用。

学校或单位管理的电脑，可能由组织统一限制浏览器或图形能力。此时应检查允许使用的环境，而不是反复修改无关的应用选项。寻求技术帮助时，提供可见错误文字，以及正常模式和兼容模式各自的表现。

只有在文件已经妥善保留，并理解本地偏好和缓存可能丢失后，才考虑清理站点数据。干净配置可以帮助诊断，但不应成为意外丢掉唯一一份打开文档的原因。

<GuideScreenshot
  base="/imgs/wordcraft/dark"
  alt="WordCraft 深色界面，插入选项卡和文档中的格式标记"
  caption="图 3：主题外观和应用启动属于不同问题。截图是深色模式的界面参考，空白画布不能直接解释为主题效果。图片来源：ArtCraft / WordCraft。"
/>

<h2 id="shortcuts">快捷键异常时先确认焦点</h2>

快捷键没有影响文档时，先点击编辑区域，输入一个字符，再尝试撤销。地址栏、浏览器自身快捷键或内嵌编辑器外的控件，都可能接收到本来希望发送给文档的键盘输入。

保存快捷键尤其需要检查结果。浏览器自己的“保存网页”窗口，不等于保存可编辑 DOCX。如果窗口建议保存网页而不是文档，回到编辑区或使用编辑器的文件菜单，并核对最终文件类型。

功能区太挤时，放大浏览器窗口或单独打开编辑器标签页。即使应用可以在手机加载，密集的文档控件也可能不适合长时间触屏操作。持续写作时，大屏幕和实体键盘通常更容易使用与验证。

文字突然过大或过小时，分别检查浏览器缩放和编辑器自己的文档缩放。它们作用在不同层面。与其他软件比较布局之前，先把这些设置恢复到明确的状态。

<h2 id="desktop">哪些情况值得尝试桌面版？</h2>

如果浏览器启动、图形策略或保存窗口反复打断工作，可以考虑桌面应用。通过[下载指南](/download)找到平台选项与官方发布，并使用同一份已保存文档的副本进行比较，减少无关变量。

网页版可以继续用于适合它的场景：快速体验、短时编辑，或不方便安装软件的电脑。根据自己的文件和实际结果决定使用方式。便利性不仅包括“快速打开编辑器”，也包括能顺利拿到完成的文件。

与其他应用交换文档时，无论网页版还是桌面版，都应执行[兼容性检查](/wordcraft-guide)。换成安装版，并不会自动取消字体、分页和收件人文件验证这些步骤。

<h2 id="questions">网页版常见问题</h2>

### 网页版一定与最新桌面版相同吗？

不一定。本站托管构建基于 v0.7.0，对比功能或反馈行为时，请分别查看构建说明和上游发布记录。

### 按下保存之后文档在哪里？

查看文件选择窗口中指定的位置，或者浏览器下载列表。主动取消窗口意味着取消保存。找到文件后重新打开，确认最近修改确实存在。

### 可以依赖浏览器缓存离线工作吗？

不要把缓存当作一定可用的离线环境或文档备份。需要依赖离线使用前，应测试自己的具体环境，并在浏览器会话之外保留已验证的文档文件。

### 同一份 DOCX 在另一台电脑上为什么换行不同？

先检查字体和页面设置，再查看表格列宽与对象位置。支持一种文件格式，并不代表在所有字体和布局环境中渲染完全一致。

### 浏览器问题解决后，接下来怎么练习？

按[在线写作指南](/wordcraft-online-guide)完成一次完整的文档练习，或回到 [WordCraft 首页](/)选择编辑器、教程与桌面下载入口。
